import { HOC_MDXWrapper } from 'nextra/setup-page'
import { pageMap } from '/vercel/path0/.next/static/chunks/nextra-page-map-.mjs'
/*@jsxRuntime automatic*/
/*@jsxImportSource react*/
import {useMDXComponents as _provideComponents} from "nextra/mdx";
const title = "Advanced Automation Guide";
const frontMatter = {
  "title": "Advanced Automation Guide",
  "description": "Pluto — The API for personal web data. Embed it in your app to authenticate users, share data and perform actions on their behalf for any site on the internet."
};
import {Callout} from "nextra/components";
export function useTOC(props) {
  return [{
    value: "Introduction to Pluto and Embedded Automations",
    id: "introduction-to-pluto-and-embedded-automations",
    depth: 2
  }, {
    value: "Example 1: Pseudo-Bank Login and Balance Proof",
    id: "example-1-pseudo-bank-login-and-balance-proof",
    depth: 2
  }, {
    value: "Script Overview",
    id: "script-overview",
    depth: 3
  }, {
    value: "Pseudo-Bank Script Code",
    id: "pseudo-bank-script-code",
    depth: 3
  }, {
    value: "Explanation",
    id: "explanation",
    depth: 3
  }, {
    value: "Types of Prompts available",
    id: "types-of-prompts-available",
    depth: 2
  }, {
    value: "Example 2: Reddit Karma Collector",
    id: "example-2-reddit-karma-collector",
    depth: 2
  }, {
    value: "a. Prompting for Login Method",
    id: "a-prompting-for-login-method",
    depth: 3
  }, {
    value: "b. Handling Reddit’s 2FA (One-Time Password)",
    id: "b-handling-reddits-2fa-one-time-password",
    depth: 3
  }, {
    value: "c. Standard Login (Username & Password)",
    id: "c-standard-login-username--password",
    depth: 3
  }, {
    value: "d. Google OAuth Login (with Passkey and 2FA handling)",
    id: "d-google-oauth-login-with-passkey-and-2fa-handling",
    depth: 3
  }, {
    value: "e. Running the Chosen Login Flow",
    id: "e-running-the-chosen-login-flow",
    depth: 3
  }, {
    value: "f. Navigating Reddit and Scraping Karma",
    id: "f-navigating-reddit-and-scraping-karma",
    depth: 3
  }, {
    value: "Explanation:",
    id: "explanation-1",
    depth: 4
  }, {
    value: "g. Proving the Data and Cleanup",
    id: "g-proving-the-data-and-cleanup",
    depth: 3
  }, {
    value: "Debugging and Development Tips",
    id: "debugging-and-development-tips",
    depth: 2
  }, {
    value: "Summary",
    id: "summary",
    depth: 2
  }];
}
function MDXLayout(props) {
  const {toc = useTOC(props)} = props;
  const _components = {
    a: "a",
    blockquote: "blockquote",
    code: "code",
    details: "details",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    li: "li",
    ol: "ol",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    summary: "summary",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return <><_components.h1>{"Pluto Automation Script Developer Guide"}</_components.h1>{"\n"}<_components.p>{"This guide is for developers building custom automation scripts using Playwright in Pluto’s secure environment. We’ll walk through two working examples (a pseudo-bank demo and a Reddit karma checker) and cover best practices for robust, error-proof automations. Topics include handling 2FA/OTP, adapting to different login flows, scraping data reliably, debugging techniques, and tips for dealing with issues like layout changes or CAPTCHAs."}</_components.p>{"\n"}<_components.h2 id={toc[0].id}>{toc[0].value}</_components.h2>{"\n"}<_components.ol>{"\n"}<_components.li>{"The most basic automation structure:"}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"import"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" { createSession } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"from"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"@plutoxyz/automation\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"import"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" { chromium } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"from"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"playwright-core\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Create a new Pluto session (secure browser context)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" session"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" createSession"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// ... your automation steps ..."}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(); "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// make sure to close the session at the end"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Every script begins by creating a session, which handles secure browser launch and user prompts, and ends by closing the session. Within the script, you’ll navigate pages, fill forms, and use session.prove(…) to output any data you want to attest."}</_components.p>{"\n"}<_components.p>{"When run inside Pluto’s environment (e.g. via Pluto Frame in your app), the script will execute in a secure browser. For local testing and development, you can also run it on your machine (more on this in the debugging section)."}</_components.p>{"\n"}<_components.p>{"With the basics in place, let’s dive into a full example."}</_components.p>{"\n"}<_components.h2 id={toc[1].id}>{toc[1].value}</_components.h2>{"\n"}<_components.p>{"Our first example is a simple script that logs into a fake bank website and retrieves the account balance. This will illustrate the fundamental structure of a Pluto automation script: prompting for user input, performing actions with Playwright, waiting for content, and proving the result."}</_components.p>{"\n"}<_components.h3 id={toc[2].id}>{toc[2].value}</_components.h3>{"\n"}<_components.p>{"The script will prompt the user for a username and password, navigate to the bank’s login page, sign in, wait for the accounts overview to load, scrape the balance, and then output (prove) the balance."}</_components.p>{"\n"}<_components.h3 id={toc[3].id}>{toc[3].value}</_components.h3>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"import"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" { createSession } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"from"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"@plutoxyz/automation\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"import"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" { chromium } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"from"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"playwright-core\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" session"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" createSession"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Prompt credentials from the user"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"username"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"password"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Login to Pseudo Bank\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter your username and password to login to Pseudo Bank\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    { label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: {} },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    { label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: {} },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"});"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Launch browser and open a new page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" browser"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" chromium."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"connectOverCDP"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"cdp"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"());"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" context"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" browser."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"contexts"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" page"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" context."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"pages"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"];"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Navigate to the login page and enter credentials"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"goto"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"https://pseudo-bank.pluto.dev\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(username);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(password);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Login\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForSelector"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text=Your Accounts\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { timeout: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"5000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" });"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Scrape the account balance from the page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" balanceLocator"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"locator"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"#balance-2\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" balanceLocator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitFor"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({ state: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"visible\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", timeout: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"5000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" balanceText"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" balanceLocator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"textContent"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()) "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"||"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" balance"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" parseFloat"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(balanceText."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"replace"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"/"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"[$,]"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"/"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"g"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"));"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Prove the bank balance (output the result)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prove"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"bank_balance\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", balance);"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Cleanup"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" browser."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h3 id={toc[4].id}>{toc[4].value}</_components.h3>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Prompting the User"}</_components.strong>{": The script uses "}<_components.code>{"session.prompt(...)"}</_components.code>{" to ask the user for their Username and Password. This generates a secure prompt (via Pluto Frame) on the user’s side, and returns the entered values. By not hardcoding credentials and using Pluto’s prompt, we ensure sensitive info is never exposed in logs or code."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Launching the Browser"}</_components.strong>{": We connect to a Chromium browser via "}<_components.code>{"chromium.connectOverCDP(await session.cdp())"}</_components.code>{". Under the hood, "}<_components.code>{"createSession()"}</_components.code>{" has started a browser in a secure enclave. Using connectOverCDP attaches our script to that browser’s devtools protocol. We retrieve the browser context and page."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Navigating and Logging In"}</_components.strong>{": "}<_components.code>{"page.goto('https://pseudo-bank.pluto.dev')"}</_components.code>{" opens the login page. We then fill the login form using Playwright locators:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li><_components.code>{"page.getByRole('textbox', { name: 'Username' })"}</_components.code>{" finds the username input field by its accessible role and label text. This is more robust than querying by CSS selectors or placeholder, because it relies on screen-reader labels which usually remain stable. We call "}<_components.code>{".fill(username)"}</_components.code>{" to type the provided username."}</_components.li>{"\n"}<_components.li>{"Similarly, fill the password field and click the Login button (found by its role and name)."}</_components.li>{"\n"}<_components.li>{"After clicking login, we wait for a known element or text that indicates a successful login. Here "}<_components.code>{"page.waitForSelector('text=Your Accounts')"}</_components.code>{" waits until the text “Your Accounts” appears on the page (with a timeout of 5 seconds). This ensures the next step runs only after the accounts overview is loaded. Always use waits after navigation or actions that trigger page loads — it’s critical for script reliability."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Scraping Data"}</_components.strong>{": Once logged in, the script needs to extract the account balance. We use a CSS selector "}<_components.code>{"#balance-2"}</_components.code>{" (an element with id “balance-2”) which we know holds the balance. We do:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li><_components.code>{"const balanceLocator = page.locator('#balance-2');"}</_components.code>{" to create a locator for that element."}</_components.li>{"\n"}<_components.li><_components.code>{"await balanceLocator.waitFor({ state: 'visible' })"}</_components.code>{" to ensure the element is present and visible."}</_components.li>{"\n"}<_components.li><_components.code>{"const balanceText = await balanceLocator.textContent()"}</_components.code>{" to grab the text content (e.g. “$8,910.30”). We then parse it by stripping out currency symbols and commas and converting to a float."}</_components.li>{"\n"}<_components.li>{"This approach is simple for a controlled example. In real scenarios, you might need to trim whitespace or handle different formats. The key is to reliably locate the element containing the data."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Proving the Result"}</_components.strong>{": Finally, we call "}<_components.code>{"session.prove('bank_balance', balance)"}</_components.code>{". This reports the balance value under the label ‘bank_balance’ to Pluto’s attestation system."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Cleanup"}</_components.strong>{": We close the page, browser, and session. Always close the session at the end of your script to release resources."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"This pseudo-bank example demonstrates the basic pattern for automation scripts. Next, we’ll examine a more complex real-world example with additional challenges."}</_components.p>{"\n"}<_components.h2 id={toc[5].id}>{toc[5].value}</_components.h2>{"\n"}{"\n"}<Callout type="info" emoji="💡"><_components.p>{"TODO: Add examples of each prompt type"}</_components.p></Callout>{"\n"}<_components.h2 id={toc[6].id}>{toc[6].value}</_components.h2>{"\n"}<_components.p>{"Our second example is an automation that logs into Reddit and proves the user’s total karma."}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Reddit allows login either with username/password or via Google OAuth – our script supports both."}</_components.li>{"\n"}<_components.li>{"Reddit accounts may have 2FA enabled – our script detects and handles OTP entry."}</_components.li>{"\n"}<_components.li>{"Logging in via Google may trigger passkey – our script deals with this by simulating a security key."}</_components.li>{"\n"}<_components.li>{"After login, Reddit’s UI (new design) requires navigating a menu to reach the profile page and then scraping the karma values."}</_components.li>{"\n"}<_components.li>{"We must account for possibly different DOM structures (e.g. if user is on the new Reddit design vs old design, or mobile vs desktop). Our example assumes the new Reddit desktop layout. We’ll discuss how layout differences can affect selectors and how to handle that."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"Let’s break down the Reddit script into parts for clarity."}</_components.p>{"\n"}<_components.h3 id={toc[7].id}>{toc[7].value}</_components.h3>{"\n"}<_components.p>{"Reddit offers multiple login methods. We start by asking the user how they want to log in – either with “Email or Username” (i.e. Reddit credentials) or “Google” (using Google OAuth). This determines which flow to follow."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  USERNAME: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or Username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  GOOGLE: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Google\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Prompt the user to choose login method"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"authType"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Log in\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"How do you want to log in?\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Login Type\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"checkbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      attributes: {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        multiple: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"false"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        options: Object."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"values"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"), "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// ['Email or Username', 'Google']"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"});"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`User chose ${"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"authType"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"]"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"}`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"The user will then select one of the options in the Pluto Frame UI."}</_components.p>{"\n"}<_components.p>{"With the choice in hand, we create a browser session similar to before:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" session"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" createSession"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" browser"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" chromium."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"connectOverCDP"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"cdp"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"());"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" context"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" browser."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"contexts"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" page"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" context."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"pages"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"];"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Now we can proceed with the login. We will handle two flows:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Standard Reddit Login (username/password, possibly with Reddit 2FA)."}</_components.li>{"\n"}<_components.li>{"Google OAuth Login (which involves a popup and Google’s flow, possibly with Google 2FA)."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"But first, one common piece: Reddit sometimes might immediately present a 2FA check if it detected a suspicious login attempt, even before credentials (this is rare, usually 2FA comes after password). The script includes a helper "}<_components.code>{"handleSecurityDetection()"}</_components.code>{" for Reddit’s own 2FA after login. We’ll call this function at appropriate points."}</_components.p>{"\n"}<_components.h3 id={toc[8].id}>{toc[8].value}</_components.h3>{"\n"}<_components.p>{"Reddit’s two-factor authentication page has a textbox for a verification code and a “Check code” button. To handle this if it appears, our script uses a detection function:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Detect if Reddit is asking for a 2FA code and handle it"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" handleSecurityDetection"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"  // Check if the 2FA verification code input is present (and visible)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" security"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" !!"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  )."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (security) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Reddit 2FA detected, prompting for security code`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" otp"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the one time code sent to your phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling security code`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(otp["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Check code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Security code submitted`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`No security code detected, continuing`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"What this does: It looks for a textbox with accessible name “Verification code”. If found, it assumes the Reddit 2FA page is present. It then prompts the user for the 6-digit code (e.g., from their authenticator app or SMS), fills it in, and clicks “Check code”, then waits for the page to load after submission. If no such textbox is found, it logs that no 2FA was needed. We will invoke "}<_components.code>{"handleSecurityDetection()"}</_components.code>{" after attempting login, to cover the case where Reddit requires a code."}</_components.p>{"\n"}<_components.p>{"Why check "}<_components.code>{".elementHandles().length"}</_components.code>{" instead of "}<_components.code>{"isVisible()"}</_components.code>{"? In our testing, we’ve found "}<_components.code>{"isVisible()"}</_components.code>{" to be somewhat unreliable. If we only need to check for the presence of an element, "}<_components.code>{".elementHandles().length"}</_components.code>{" will suffice. "}<_components.code>{"isVisible()"}</_components.code>{" is only required if we need to ensure the element is actually visible to the user. In addition, we use "}<_components.code>{"elementHandles().length"}</_components.code>{" over "}<_components.code>{"elementHandle()"}</_components.code>{" because the latter will throw an error if the element is not found, which we don’t want."}</_components.p>{"\n"}<_components.h3 id={toc[9].id}>{toc[9].value}</_components.h3>{"\n"}<_components.p>{"Now, the function for the normal Reddit login using username/password:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authUsernamePass"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"usernameError"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"passwordError"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting user for username/password`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"username"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"password"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Login to Reddit\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter your Reddit credentials to prove your data\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", min_length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"3"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        error: usernameError,"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        error: passwordError,"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling username`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(username);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling password`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(password);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Log In\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"2000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"); "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// small wait for page to respond"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"  // Check if a 2FA code input became visible (Reddit 2FA)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" has2FA"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"isVisible"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (has2FA) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Detected 2-Step Verification (OTP) for Reddit login`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the one time code from your authenticator app\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Check code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`2FA code submitted to Reddit`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`No 2FA required for Reddit login`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Key points in this "}<_components.code>{"authUsernamePass"}</_components.code>{" flow:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"We prompt the user for their Reddit credentials. Notice we include optional "}<_components.code>{"error"}</_components.code>{" fields for the prompts. If the login fails (e.g., wrong password), we could call "}<_components.code>{"authUsernamePass"}</_components.code>{" again and supply an error message to display to the user."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"We fill the login form using "}<_components.code>{"getByRole"}</_components.code>{" locators similar to the bank example."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"After clicking “Log In”, we wait 2 seconds with "}<_components.code>{"waitForTimeout(2000)"}</_components.code>{". This is a simple way to give the page time to either navigate or reveal an error. A more robust approach might be "}<_components.code>{"await page.waitForURL()"}</_components.code>{" to detect if we moved off the login page, or wait for some element that only appears on success or failure. The short wait helps ensure the next check doesn’t execute too early."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"We then check for Reddit’s OTP prompt using "}<_components.code>{".isVisible()"}</_components.code>{" on the “Verification code” field. Here we use "}<_components.code>{"isVisible()"}</_components.code>{" instead of checking existence because (unlike earlier) Reddit’s login page always includes the 2FA field in the DOM (hidden with CSS if 2FA is not needed). So we must check visibility to know if it’s actually being asked for. If 2FA is required, we prompt the user for their authenticator code, fill it, and click “Check code”."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"If no 2FA, we simply continue. At this point, the user is logged in (or if credentials were wrong, technically still on login page — our script does not yet handle that scenario with a retry, which could be an enhancement)."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.h3 id={toc[10].id}>{toc[10].value}</_components.h3>{"\n"}<_components.p>{"If the user chose to log in with Google, the flow is more complex because it involves a popup window and potentially various Google security steps. The script’s "}<_components.code>{"authGoogle"}</_components.code>{" function manages this:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authGoogle"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Continue with Google\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Opening Google login popup`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" googlePage"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForEvent"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"popup\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Google login popup opened`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"  // **Bypass WebAuthn passkey prompts by using a virtual authenticator**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" client"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" context."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"newCDPSession"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(googlePage);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" client."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"send"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"WebAuthn.enable\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" client."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"send"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"WebAuthn.addVirtualAuthenticator\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    options: { protocol: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"ctap2\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", transport: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"internal\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Virtual authenticator added to disable passkey prompts`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"  // Prompt user for their Google account credentials"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"email"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"password"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Google Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter your Google credentials to login\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", min_length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"3"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling Google email`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(email);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForNavigation"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"1000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"); "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// wait for password field to appear"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"  // If \"Try another way\" is present, the account might have passkey enabled"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Try another way\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"catch"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(() "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {});"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling Google password`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter your password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(password);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForNavigation"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({ waitUntil: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"networkidle\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"3000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"  // Check if Google asks for 2-Step Verification"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" has2Step"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" !!"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"2-Step Verification\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  )."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (has2Step) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Google account has 2-Step Verification enabled`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // Google might present multiple 2FA options (app prompt, authenticator code, SMS)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // We will detect available options and prompt user to choose one."}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // Optional screen: sometimes Google shows a \"use your device / try another way\" interim step"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" optionalPrompt"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"      \"fingerprint, face, or screen lock\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    );"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" optionalPrompt."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"())."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"        `Google presented an extra verification option screen, skipping it`"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      );"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Try another way\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"1000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // Define possible 2FA options"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" options"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Tap Yes on your phone or tablet\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        locator: googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"link\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\" on your phone or tablet\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"        followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"          // This option is just a prompt on user's device; we'll ask user to confirm they did it"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"        prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Confirm Access\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          description:"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"            'Please tap \"Yes\" on your phone to allow sign-in, then confirm here to continue.'"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"              label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Confirm to continue\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"              type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"checkbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"              attributes: { options: ["}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Confirmed\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"], required: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"true"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code from the Google Authenticator app\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        locator: googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"link\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code from\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"        followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_page"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"          // This will execute after user provides code"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"          await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(_code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"          await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"        prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the code from your Google Authenticator app\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code sent to your phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        locator: googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"link\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code at\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"        followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_page"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"          await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(_code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"          await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"        prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the one time code sent to your phone via SMS\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // Filter which options are actually enabled on this account"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" enabledOptions"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" [];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    for"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" opt"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" of"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" options) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        ("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" opt.locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"())."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" &&"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        !"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" opt.locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"isDisabled"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"())"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      ) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"push"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(opt);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ==="}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" 0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"warn"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`No 2FA options available or enabled, this is unexpected.`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ==="}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" 1"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"      // Only one option available, proceed with it"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Single 2FA option detected: ${"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"enabledOptions"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"]."}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"label"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"}`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"].locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting user for code (if needed)`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"());"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"].followup) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(googlePage, code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`2FA code entered for Google`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"      // Multiple options, ask user to choose"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"        `Multiple 2FA options detected for Google: ${"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"enabledOptions"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"          ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"map"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"(("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"o"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" o"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"label"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"          ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"join"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\", \""}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{")"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"}`"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      );"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"choice"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Two Factor Authentication\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Choose a 2FA method to continue Google login\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"2FA Method\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"checkbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            attributes: {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"              multiple: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"false"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"              options: enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"map"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"o"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" o.label),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" selected"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"find"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"o"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" o.label "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"==="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" choice["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" selected.locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`User chose 2FA method: ${"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"selected"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"label"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"}`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" selected."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"());"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (selected.followup) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" selected."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(googlePage, code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`2FA step completed for Google`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Google account did not require 2-Step Verification`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"This is the most involved part of the script, so let’s unpack it:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"We click the “Continue with Google” button on Reddit’s login page. Playwright captures the popup that opens ("}<_components.code>{"await page.waitForEvent('popup')"}</_components.code>{") as "}<_components.code>{"googlePage"}</_components.code>{". Now we have a separate "}<_components.code>{"googlePage"}</_components.code>{" context to automate the Google login."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Bypassing Passkey Prompts"}</_components.strong>{": New Google sign-ins may prompt the user to use a passkey or a device (e.g., on Android you might get “Use your screen lock or fingerprint”). Since our automation cannot complete those, we preemptively add a virtual authenticator via Chrome DevTools Protocol:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"We obtain a CDP session for the "}<_components.code>{"googlePage"}</_components.code>{" (with "}<_components.code>{"context.newCDPSession(googlePage)"}</_components.code>{")."}</_components.li>{"\n"}<_components.li>{"Enable the WebAuthn domain and add a virtual authenticator ("}<_components.code>{"WebAuthn.addVirtualAuthenticator"}</_components.code>{") with CTAP2 protocol. This effectively registers a fake security key with the page that auto-accepts any passkey request. This means if Google tries to initiate a passkey login, it will get an immediate dummy response (preventing a deadlock waiting for user’s biometric action)."}</_components.li>{"\n"}<_components.li>{"We log that we added the virtual authenticator to inform our debugging."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"This trick is crucial if the user has passkeys enabled on their Google account. Without it, the script would hang at a passkey prompt."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Google account credentials"}</_components.strong>{": We prompt the user for their Google email and password. Then:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Wait for the Google login page to load, fill the email/phone field, click Next."}</_components.li>{"\n"}<_components.li>{"After navigation, wait a moment for the password field and possibly a “Try another way” option. We call "}<_components.code>{".click()"}</_components.code>{" on “Try another way” and ignore errors if it’s not present. This step is there to bypass any lingering prompt (for instance, if Google initially wants to use a stored credential or device, clicking “Try another way” forces it to ask for password)."}</_components.li>{"\n"}<_components.li>{"Fill the password and click Next, then wait for navigation to complete (using "}<_components.code>{"networkidle"}</_components.code>{" state to ensure all network calls settle). At this point, if credentials are correct, either the Google login is done (if no 2FA) or Google will proceed to a 2-Step Verification step."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Handling Google’s 2-Step Verification"}</_components.strong>{": We check if the text “2-Step Verification” is present. If yes, the Google account has 2FA. Google might offer multiple methods (like prompt, authenticator code, SMS code, backup codes, etc.). Our script:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"Checks for an intermediate prompt like “use your fingerprint, face, or screen lock” – if found, it clicks “Try another way” to skip that, because we can’t handle device biometrics in automation."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Defines possible 2FA options we know how to handle:"}</_components.p>{"\n"}<_components.ol>{"\n"}<_components.li>{"“Tap Yes on your phone or tablet” – this is the default push notification Google prompt. Our approach is to have the user tap yes on their device and then confirm in our UI they did it. We can’t automate the phone tap, obviously, so we rely on the user. The prompt for this option asks the user to confirm once they’ve approved the login on their device."}</_components.li>{"\n"}<_components.li>{"Authenticator App code – Google Authenticator or similar. The script will prompt the user for the 6-digit code, then fill it in and click Next."}</_components.li>{"\n"}<_components.li>{"SMS code – similar to authenticator, but code sent via SMS."}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.p>{"(You could extend this with backup codes or other methods if needed.)"}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"It then checks which of these options are actually visible and enabled on the page. For example, if the user has an authenticator app configured, that option’s link will appear. If not, it might be absent or disabled. We gather the available options into "}<_components.code>{"enabledOptions"}</_components.code>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"If no known options are available (which would be unusual), we log a warning. If exactly one option is available, we proceed with it automatically. If multiple are available, we prompt the user to pick one (similar to how we prompted for login method)."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"After the user either confirms or provides a code depending on the method, we run the appropriate follow-up: e.g., fill the code into the page and submit. This completes the Google login flow."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"This Google login handling illustrates how to script around unpredictable security flows by:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"detecting what is presented,"}</_components.li>{"\n"}<_components.li>{"offering the user choices or prompts as needed,"}</_components.li>{"\n"}<_components.li>{"and leveraging browser capabilities (like CDP for WebAuthn) to handle things programmatically where possible."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.blockquote>{"\n"}<_components.p><_components.strong>{"Tip"}</_components.strong>{": Virtual Authenticators for Passkeys: The use of a virtual authenticator to bypass passkey or WebAuthn prompts can be reused for other sites that use WebAuthn (passkeys or security keys). It essentially automates a hardware key. In Playwright, you can enable it via CDP as shown. This is an advanced technique, but extremely useful when automating logins for accounts that have passkeys enabled. Without it, the script could be blocked by a modal waiting for user interaction on the physical device."}</_components.p>{"\n"}</_components.blockquote>{"\n"}<_components.h3 id={toc[11].id}>{toc[11].value}</_components.h3>{"\n"}<_components.p>{"After defining "}<_components.code>{"authUsernamePass"}</_components.code>{" and "}<_components.code>{"authGoogle"}</_components.code>{", and the "}<_components.code>{"handleSecurityDetection"}</_components.code>{" helper, our script triggers the correct flow based on the user’s choice:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Navigate to Reddit login page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" LOGIN_URL"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"https://www.reddit.com/login/\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"goto"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"LOGIN_URL"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { waitUntil: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`${"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"LOGIN_URL"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"} loaded`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Handle any immediate security page (unlikely on initial load)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" handleSecurityDetection"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Execute the chosen auth flow"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"switch"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (authType["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  case"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"GOOGLE"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Proceeding with Google OAuth flow`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authGoogle"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // Wait for Reddit to possibly redirect after Google login"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // If Reddit now shows its 2FA (uncommon after Google login, but just in case)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" handleSecurityDetection"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    break"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  case"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"USERNAME"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  default"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Proceeding with username/password flow`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authUsernamePass"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"    // (Reddit 2FA handled inside authUsernamePass)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    break"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`User logged in to Reddit`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"We load the Reddit login page and immediately call "}<_components.code>{"handleSecurityDetection()"}</_components.code>{" in case Reddit threw a security check upfront (generally it won’t, but it’s there for completeness). Then:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"If the user chose Google, we call "}<_components.code>{"authGoogle()"}</_components.code>{". After it returns, the Google popup has closed and the main page should be logged in to Reddit (Google handled authentication and Reddit redirected back). We wait for the content to load and call "}<_components.code>{"handleSecurityDetection()"}</_components.code>{" once more in case Reddit still asks for its own 2FA after the OAuth (usually not)."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"If the user chose username, we call "}<_components.code>{"authUsernamePass()"}</_components.code>{", which handles Reddit login (and calls its own 2FA prompt if needed)."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"After either flow, we consider the user logged in."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"At this point, if everything succeeded, we have an authenticated Reddit session on "}<_components.code>{"page"}</_components.code>{". If credentials were wrong or a step failed, the script might still be on the login page or an error state. In a production script, you’d want to detect and handle failures (e.g., by checking the current URL or page content to ensure login success and maybe re-prompt or abort with an error message). For brevity, our example assumes success for the demonstration."}</_components.p>{"\n"}<_components.h3 id={toc[12].id}>{toc[12].value}</_components.h3>{"\n"}<_components.p>{"Now that we’re logged in, the next steps are to go to the user’s profile page and scrape the karma values."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Navigate to the user's profile page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Navigating to User Profile`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name:"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" /"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#DBEDFF"
  }}>{"expand user menu"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"$"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"/"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"i"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"View Profile\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Wait for profile page to load"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Profile page loaded`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Scrape the Reddit username and karma"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Scraping user data`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" redditUsername"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByLabel"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Profile information\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"heading\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"first"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"innerText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"postKarma"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"commentKarma"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByTestId"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"karma-number\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"allInnerTexts"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Calculate total karma"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" totalKarma"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"  Number"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(postKarma."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"replace"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\",\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")) "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"+"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" Number"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(commentKarma."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"replace"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\",\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"))"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"toLocaleString"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"en-US\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h4 id={toc[13].id}>{toc[13].value}</_components.h4>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Navigating to profile"}</_components.strong>{": We click on the user menu. On new Reddit’s interface, the user menu is opened by clicking the user avatar button. The code uses a regex in the locator name ("}<_components.code>{"/expand user menu$/i"}</_components.code>{") to find the button whose name ends with “Expand user menu” (case-insensitive). This is an example of using regex in "}<_components.code>{"getByRole"}</_components.code>{" to handle dynamic parts (the username might be part of the button label in some cases, hence just matching the end)."}</_components.p>{"\n"}<_components.p>{"Note: If the account is brand new or has no avatar, the label might differ, but generally this works for Reddit’s UI. Alternatively, one could directly go to "}<_components.code>{"reddit.com/user/<username>"}</_components.code>{" if the username is known, but we obtain the username only after login. Clicking the UI ensures we follow the intended flow and end up in the right place."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Scraping username"}</_components.strong>{": Reddit’s profile page (new design) has the profile information section containing the display name. We use a chained locator: "}<_components.code>{"page.getByLabel('Profile information').getByRole('heading').first()"}</_components.code>{". This finds the element labeled “Profile information” (which is an accessible label on the container of profile details), then within that container finds the first heading. That happens to be the username (the Reddit handle of the user) displayed on the profile. We then get its inner text."}</_components.p>{"\n"}<_components.p>{"This approach is a bit more robust than something like "}<_components.code>{"page.getByText('u/')"}</_components.code>{" because it anchors on a known labeled section. It demonstrates how to scope locators to a certain section of the page for reliability."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Scraping karma"}</_components.strong>{": Reddit shows two karma numbers (Post Karma and Comment Karma) on the profile. In the DOM, they are marked with "}<_components.code>{"data-testid=\"karma-number\""}</_components.code>{". We use "}<_components.code>{"getByTestId('karma-number')"}</_components.code>{" which returns both elements. By calling "}<_components.code>{".allInnerTexts()"}</_components.code>{", we get an array of the text content of each matching element. The order should correspond to post and comment karma. We assign them to "}<_components.code>{"[postKarma, commentKarma]"}</_components.code>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Computing total karma"}</_components.strong>{": We parse the two strings to integers (removing commas) and sum them, then format the sum as a localized string (to add comma separators for thousands)."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"Now we have the data we want: "}<_components.code>{"redditUsername"}</_components.code>{", "}<_components.code>{"postKarma"}</_components.code>{", "}<_components.code>{"commentKarma"}</_components.code>{", and "}<_components.code>{"totalKarma"}</_components.code>{"."}</_components.p>{"\n"}<_components.h3 id={toc[14].id}>{toc[14].value}</_components.h3>{"\n"}<_components.p>{"Finally, we output the data via "}<_components.code>{"session.prove"}</_components.code>{" and close everything:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prove"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"reddit-data\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  { redditUsername: redditUsername },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  { totalKarma: totalKarma },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  { postKarma: postKarma },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  { commentKarma: commentKarma },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Proof generated, closing session`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" browser."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"We call "}<_components.code>{"session.prove"}</_components.code>{" with a key ‘reddit-data’ and an array of key-value pairs as the value. This is how you can structure multiple pieces of data in one proof. In the Pluto attestation, this might appear as a JSON array or simply multiple outputs under the ‘reddit-data’ proof. Essentially, we are proving a collection of values:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li><_components.code>{"redditUsername"}</_components.code>{" (the account’s username),"}</_components.li>{"\n"}<_components.li><_components.code>{"totalKarma"}</_components.code>{" (sum of karma),"}</_components.li>{"\n"}<_components.li><_components.code>{"postKarma"}</_components.code>{","}</_components.li>{"\n"}<_components.li><_components.code>{"commentKarma"}</_components.code>{"."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"After that, we gracefully close the page, browser, and session."}</_components.p>{"\n"}<_components.p>{"This completes the Reddit example script. It’s lengthy, but it showcases many techniques for robust automation:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"prompting and branching logic for different flows,"}</_components.li>{"\n"}<_components.li>{"waiting for events (popup, navigation, network idle),"}</_components.li>{"\n"}<_components.li>{"handling multi-step authentication,"}</_components.li>{"\n"}<_components.li>{"scraping using reliable selectors,"}</_components.li>{"\n"}<_components.li>{"and outputting structured results."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.details><_components.summary>{"📄 "}<_components.strong>{"View Full Reddit Script Code"}</_components.strong>{" (for reference)"}</_components.summary><_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"import"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" { createSession } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"from"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"@plutoxyz/automation\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"import"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" { chromium } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"from"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"playwright-core\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  USERNAME: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or Username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  GOOGLE: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Google\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" LOGIN_URL"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" \"https://www.reddit.com/login/\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Detect if we run into a Reddit 2FA page and handle it"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" handleSecurityDetection"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" security"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" !!"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  )."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (security) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Reddit 2FA detected, prompting for security code`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" otp"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the one time code sent to your phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling security code`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(otp["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Check code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`No security code detected, continuing`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" promptAuthType"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Log in\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"How do you want to log in\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Login Type\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"checkbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { multiple: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"false"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", options: Object."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"values"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" session"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" createSession"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting for auth type`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"authType"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" promptAuthType"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`User chose ${"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"authType"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"]"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"}`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Browser Setup"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" browser"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" chromium."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"connectOverCDP"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"cdp"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"());"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" context"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" browser."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"contexts"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" page"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" context."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"pages"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Browser opened`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Standard Reddit Login (Username/Password) with optional 2FA"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authUsernamePass"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"usernameError"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"passwordError"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting user for username/password`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"username"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"password"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Login to Reddit\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter your Reddit credentials to prove your data\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", min_length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"3"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        error: usernameError,"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        error: passwordError,"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling username`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or username\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(username);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling Password`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(password);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Log In\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"2000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" has2FA"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"isVisible"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (has2FA) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Detected 2-Step Verification`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the one time code from your authenticator app\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Verification code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Check code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`No 2-Step Verification detected, continuing`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"/**"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" * Google OAuth Login flow with passkey/2FA handling"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{" */"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authGoogle"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Continue with Google\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Opening popup`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" googlePage"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForEvent"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"popup\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Popup opened`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"  // Disable Passkeys by adding a virtual authenticator"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" client"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" context."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"newCDPSession"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(googlePage);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" client."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"send"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"WebAuthn.enable\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" client."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"send"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"WebAuthn.addVirtualAuthenticator\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    options: { protocol: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"ctap2\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", transport: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"internal\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Passkeys disabled`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting user for credentials`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"email"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"password"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Google Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter your Google credentials to login\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", min_length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"3"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        attributes: { placeholder: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling Email`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email or phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(email);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForNavigation"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"1000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Try another way\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"catch"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(() "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {});"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Filling password`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter your password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(password);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Password filled`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForNavigation"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"networkidle\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"3000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" has2FA"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" !!"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"2-Step Verification\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  )."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (has2FA) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Detected 2-Step Verification`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" hasOptionalScreen"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"      \"fingerprint, face, or screen lock\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    );"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"!!"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" hasOptionalScreen."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"())."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Detected extra 2fa screen, continuing`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Try another way\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForTimeout"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"1000"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" tabletOption"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Tap Yes on your phone or tablet\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      locator: googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"link\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\" on your phone or tablet\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"      followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_page"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {},"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"      prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Confirm Access\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Confirm you allowed access in your Google App\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Confirm to continue\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"checkbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            attributes: { required: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"true"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", options: ["}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Confirm\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    };"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" authenticator"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code from the Google Authenticator app\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      locator: googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"link\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code from\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"      followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_page"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(_code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"      prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the one time code sent to your phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    };"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" smsCode"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code sent to your phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      locator: googlePage."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"link\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Get a verification code at\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"      followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_page"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"_code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(_code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" _page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Next\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"      prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": () "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter one time code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Enter the one time code sent to your phone\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        prompts: [{ label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Code\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"text\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", attributes: { length: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"6"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" } }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    };"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" options"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" [tabletOption, authenticator, smsCode];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" enabledOptions"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" [];"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    for"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" option"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" of"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" options) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" option.locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"elementHandles"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"())."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"!"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" option.locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"isDisabled"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"())) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"push"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(option);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" >"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" 1"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting user with enabled 2-Factor options.`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"choice"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        title: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Two Factor Authentication\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        description: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Choose how you'd like to 2FA\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        prompts: ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            label: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Two Factor Method\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            type: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"checkbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            attributes: {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"              multiple: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"false"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"              options: enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"map"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"o"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" o.label),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"            },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"          },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        ],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Selecting 2FA method`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" selected"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"find"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"i"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" i.label "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"==="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" choice["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" selected.locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting user for 2FA`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" selected."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"());"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (selected.followup) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Entering 2FA Code`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" selected."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(googlePage, code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`2FA code entered`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (enabledOptions."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"length"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ==="}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" 1"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"        `Found a single enabled option: ${"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"]."}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"locator"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"innerText"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"()"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"}`"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      );"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"].locator."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Prompting user for 2FA`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"code"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prompt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"());"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"      if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"].followup) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Entering 2FA Code`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"        await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" enabledOptions["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"followup"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(googlePage, code);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"        console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`2FA code entered`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`No enabled options found.`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"      // No 2FA options available (unexpected scenario)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Google account did not require 2FA`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Go to Reddit login page and attempt login"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Navigating to ${"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"LOGIN_URL"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"}`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"goto"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"LOGIN_URL"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { waitUntil: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" handleSecurityDetection"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(); "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// check if Reddit immediately asks for OTP"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"switch"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (authType["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"0"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  case"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"GOOGLE"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Authing Google`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authGoogle"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" handleSecurityDetection"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    break"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  case"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" AUTH_TYPES"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"USERNAME"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  default"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" authUsernamePass"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    break"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`User Logged In`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Navigate to profile page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name:"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" /"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#DBEDFF"
  }}>{"expand user menu"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"$"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"/"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"i"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"View Profile\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Scrape Username and Karma"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"waitForLoadState"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"domcontentloaded\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`User Profile Loaded`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"`Scraping Username`"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" handle"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByLabel"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Profile information\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"heading\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"first"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"()"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"innerText"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"postKarma"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"commentKarma"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"] "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByTestId"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"karma-number\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  ."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"allInnerTexts"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Prove the data"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"prove"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"reddit-data\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  { redditUsername: handle },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"    totalKarma"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": ("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      Number."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"parseInt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(postKarma."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"replace"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\",\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"), "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"10"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"+"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      Number."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"parseInt"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(commentKarma."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"replace"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\",\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"), "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"10"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    )."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"toLocaleString"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"en-US\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"),"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  { postKarma },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  { commentKarma },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"]);"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" session."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"close"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span></_components.code></_components.pre></_components.details>{"\n"}<_components.h2 id={toc[15].id}>{toc[15].value}</_components.h2>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"Be sure to try out our "}<_components.a href="https://playground.pluto.xyz/">{"Script Editor"}</_components.a>{". It has built-in logging, auto-completion, debugging, and examples."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"When writing automation scripts for Pluto, you’ll inevitably encounter cases where things don’t work on the first try — a selector might be wrong, a page may present an unexpected prompt, or a script might get stuck waiting for something. Below are tips and best practices to debug and harden your scripts."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"When looking for the existence of an element, if you only care if it exists in the DOM, and don’t care if the user is able to see it, use "}<_components.code>{".elementHandles().length"}</_components.code>{" instead of "}<_components.code>{"isVisible()"}</_components.code>{", as it is more reliable."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Prefer finding locators by role, label, or test id, as these are more stable than by text or CSS selectors."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Be sure to check out the different ways Playwright is able to wait, such as "}<_components.code>{"waitForLoadState"}</_components.code>{", "}<_components.code>{"waitForFunction"}</_components.code>{", and "}<_components.code>{"waitForURL"}</_components.code>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Build and test step-by-step"}</_components.strong>{": Develop your automation one page or step at a time. For example, first get the login working. Once that’s solid, add the next navigation and data scraping. This incremental approach makes it easier to isolate issues. As one of our developers put it: “Go page by page in your flow, making sure each step works before moving to the next.” If possible, modularize your code by breaking it into functions for each section (login, navigate, scrape, etc.), which you can test individually."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Use Chrome DevTools to inspect the live DOM"}</_components.strong>{": To reliably select elements, don’t rely on “View Page Source” – that often shows the initial HTML but not the dynamic state after scripts run. Instead, open the site in a real browser and use Inspect Element to examine the DOM and find unique attributes or accessible roles for your targets. Copy outer HTML or relevant snippets of the DOM. This is the HTML you want to base your selectors on."}</_components.p>{"\n"}<_components.blockquote>{"\n"}<_components.p>{"Why not View Source? Many modern sites (including Reddit) load content dynamically or modify the DOM after initial load. The static source may omit crucial elements (especially if content is behind an authenticated session or requires scrolling to appear). Always inspect the live DOM when possible."}</_components.p>{"\n"}</_components.blockquote>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Leverage Playwright Codegen for quick selector hints"}</_components.strong>{": Playwright has a great tool to help generate selectors. Run "}<_components.code>{"npx playwright codegen <url>"}</_components.code>{" to open the site in a special mode where you can interact with it. As you click around, it will suggest Playwright code (in JavaScript) for those actions, including the selectors used. This can be invaluable for tricky elements. You can copy the selectors (or even entire actions) and adapt them into your Pluto script. Codegen uses Playwright’s best practices (like getByRole when applicable), giving you a head start."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.details><_components.summary>{"💡 "}<_components.strong>{"Tip: Using Playwright Codegen"}</_components.strong></_components.summary><_components.p>{"When you run "}<_components.code>{"npx playwright codegen https://www.example.com"}</_components.code>{", a browser window and a code window will appear. Perform the steps you’re trying to automate (e.g., fill login form, click buttons). The code window will show lines like:"}</_components.p><_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Email\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"test@example.com\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"textbox\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Password\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"fill"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"mypassword\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" page."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"getByRole"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"button\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", { name: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"Log In\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" })."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"click"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span></_components.code></_components.pre><_components.p>{"You can even change the language (Playwright supports Python, C#, etc., but we use JS). While you still need to integrate these into your Pluto script structure (with session.prompt, session.prove, etc.), codegen is excellent for discovering how to target elements or what Playwright’s recommended selector is for a given element. It can save time and ensure you use resilient selectors."}</_components.p></_components.details>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Logging is your friend"}</_components.strong>{": Use "}<_components.code>{"console.log()"}</_components.code>{" generously to trace the execution of your script. In the examples above, you see logs for each major step (e.g., “Filling password”, “Detected 2FA”, “Profile page loaded”). When running the script via Pluto Frame or even locally, these logs will help you understand where it might be failing or stuck. If a script stops unexpectedly, check the last log printed – it often indicates the step that didn’t complete."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Use waits and timeouts appropriately"}</_components.strong>{": A common mistake is assuming something will happen instantly. Always wait for navigations ("}<_components.code>{"page.waitForNavigation"}</_components.code>{" or "}<_components.code>{"waitForURL"}</_components.code>{" or at least "}<_components.code>{"waitForLoadState"}</_components.code>{"), and wait for elements to appear before interacting. If an element might take time to load (due to network or animations), use "}<_components.code>{"waitForSelector"}</_components.code>{" with a reasonable timeout or "}<_components.code>{"locator.waitFor()"}</_components.code>{". If you’re not sure what to wait for, using a short "}<_components.code>{"waitForTimeout"}</_components.code>{" as a crude delay can sometimes help, but prefer deterministic waits when possible (like waiting for a specific element or text that indicates readiness). If an action has a built-in promise (e.g., "}<_components.code>{"click()"}</_components.code>{" will wait for navigation by default in some cases), consider that too."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Handling element not found errors"}</_components.strong>{": If your script throws an error like “No element found for selector…” or times out waiting for an element:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Double-check the selector or locator. It might be incorrect or misspelled. Use DevTools inspector or codegen to verify the element’s attributes."}</_components.li>{"\n"}<_components.li>{"Consider the context: Is the element inside an iframe or shadow DOM? (Playwright’s getByRole and other high-level selectors usually pierce iframes if same origin, but not cross-domain iframes.)"}</_components.li>{"\n"}<_components.li>{"Ensure that the step that loads that element was successful. For instance, if login failed, the profile page won’t ever load the element you expect. So the real issue might be earlier."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Dealing with different site layouts or modes"}</_components.strong>{": Some websites have very different DOM structure depending on user settings or agent:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Reddit for example has an “Old Reddit” design (if a user opts for the old interface, or if you navigate to old.reddit.com). Our script assumes the new design. If you needed to support old Reddit, you’d have to use completely different selectors (the old site is not React-based and has different IDs/classes). One approach is to detect which version is loaded (e.g., "}<_components.code>{"if (await page.locator('body[class*=\"old-Reddit\"]').count() > 0) { /* use old flow */ } else { /* new flow */ }"}</_components.code>{"). Often, it’s easiest to ensure you use the version you expect by using specific URLs or settings."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Anticipating and handling 2FA/OTP flows"}</_components.strong>{": As seen, always consider that a user may have two-factor auth enabled. If you write a script for a site with optional 2FA, include checks for those elements and prompt the user for the code. It’s fine if the user doesn’t have 2FA; the check will just skip. But if you don’t include it and a user does have 2FA, your script will fail for them. Our Reddit and Google examples both show patterns for handling OTP. Generally:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Check for an OTP input or prompt after login attempt."}</_components.li>{"\n"}<_components.li>{"If present, "}<_components.code>{"session.prompt"}</_components.code>{" the user for the code (with an appropriate message so they know to get it from their device)."}</_components.li>{"\n"}<_components.li>{"Possibly handle multiple methods if the site provides (as we did for Google)."}</_components.li>{"\n"}<_components.li>{"Testing tip: It can be tricky to test OTP flows because once you successfully verify a device, some sites won’t ask again for a while. You can often force it by using a fresh session/storage (Pluto’s sessions are isolated, but if you reuse the same session cache it might have a cookie marking the device as trusted). Use "}<_components.code>{"session.clearCache()"}</_components.code>{" or start a new session to simulate a first-time login for OTP testing."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Cloudflare or CAPTCHA blocks"}</_components.strong>{": A few sites might present anti-bot challenges (like Cloudflare IUAM page or reCAPTCHA) when they detect automation. This is a tough scenario. If your script consistently hits a CAPTCHA:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Recognize that currently full automation may not be possible without solving the CAPTCHA. You might detect it (e.g., a URL containing /challenge or presence of CAPTCHA iframe) and then decide to fail gracefully or notify the user."}</_components.li>{"\n"}<_components.li>{"We are working on integrating CAPTCHA solving in the platform, but until that’s available, you may have to avoid or manually intervene on such sites. Slowing down the navigation or using known headers might reduce detection, but there’s no guarantee."}</_components.li>{"\n"}<_components.li>{"If it’s a Cloudflare browser challenge, sometimes simply waiting (as our code does in some examples) might get past it after a few seconds, or a retry might succeed. But be cautious: hitting these means the site is hostile to automation."}</_components.li>{"\n"}<_components.li>{"Security note: Avoid any solutions that require exposing user credentials to third-party CAPTCHA solver services without user consent."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Security best practices"}</_components.strong>{":"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Never log sensitive information. Our examples log actions but never the actual passwords or codes. Be mindful if you catch errors or print variables."}</_components.li>{"\n"}<_components.li>{"Use "}<_components.code>{"type: 'password'"}</_components.code>{" in prompts for secrets so that they are masked in the UI."}</_components.li>{"\n"}<_components.li>{"Clean up any credentials from memory if possible (though in a short-lived script this is less of an issue)."}</_components.li>{"\n"}<_components.li>{"Understand that while the automation runs in a secure enclave, any data you "}<_components.code>{"console.log"}</_components.code>{" or "}<_components.code>{"session.prove"}</_components.code>{" will leave that enclave in some form (attested output or logs). Only output what is necessary for your application logic."}</_components.li>{"\n"}<_components.li>{"If dealing with financial or personal data, make sure to handle it according to user consent and regulatory requirements. Pluto provides attestation to prove the data origin, but you as the developer should still treat the data carefully."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Testing and iteration"}</_components.strong>{": It’s a good idea to test your script with multiple accounts if possible (e.g., one with 2FA, one without, one with different settings like dark mode or old Reddit, etc.). This helps uncover edge cases. Keep in mind that sites update their UIs periodically – what works today might break if the site changes a button text or layout. Maintenance is part of writing automation scripts; having good logging and a clear structure will make it easier to update scripts when needed."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"By following these practices, you’ll be able to create Pluto automation scripts that are robust and handle the many quirks of web automation. Happy scripting!"}</_components.p>{"\n"}<_components.hr />{"\n"}<_components.h2 id={toc[16].id}>{toc[16].value}</_components.h2>{"\n"}<_components.p>{"We covered how to write and debug Pluto automation scripts using Playwright, with a deep dive into two examples. The pseudo-bank example showed a straightforward login and data retrieval. The Reddit example demonstrated handling multiple login methods, 2FA, conditional flows, and data scraping. Along the way, we discussed strategies for selecting DOM elements reliably (using roles, labels, test ids), dealing with dynamic content, and anticipating security measures. We also provided tips for debugging, such as using "}<_components.code>{"npx playwright codegen"}</_components.code>{", console logging, and testing stepwise. By applying these examples and tips, developers can confidently build custom automations for a variety of sites and scenarios."}</_components.p></>;
}


export default HOC_MDXWrapper(
  MDXLayout,
  '/guides/advanced-automation',
  {"filePath":"pages/guides/advanced-automation.mdx","timestamp":1750278560000,pageMap,frontMatter,title},
  typeof RemoteContent === 'undefined' ? useTOC : RemoteContent.useTOC
)